import * as React from 'react';
import { toast } from 'sonner';
import { ApiRequestError } from './api-client';
/**
 * What a member reads when the request never reached H2HSpark and the screen offered no sentence
 * of its own. Ours, in the product's voice, and it names the one thing the reader can act on.
 */
export const NETWORK_UNAVAILABLE = 'We could not reach H2HSpark. Check your connection and try again.';
/** The last resort: a failure that is not an `Error` and carries nothing to show. */
export const SOMETHING_WENT_WRONG = 'Something went wrong.';
/**
 * Which sentence a failure is shown as.
 *
 * Separated from the hook so the decision can be tested as a decision — see
 * `__tests__/a-failure-reads-in-our-own-words.test.ts`. The hook below is a `useCallback` around
 * this and a `toast.error`, and nothing else.
 *
 * ## The order, and why it is this order
 *
 * 1. **An API refusal** speaks with the envelope message the API sent. That is the only sentence
 *    on this path written by somebody who knew what actually went wrong, and it is more specific
 *    than any fallback a screen can hold. `ApiRequestError.toUserMessage` falls back to its own
 *    status line when the body carried none.
 *
 * 2. **A request that never arrived** shows the *screen's* sentence, never its own. `fetch`
 *    rejects with a `TypeError` whose message is the browser's — *"Failed to fetch"* in Chrome,
 *    *"Load failed"* in Safari, *"NetworkError when attempting to fetch resource."* in Firefox —
 *    and none of the three is written for a member. **TW 44145597** is that text reaching one:
 *    *Your Events* passes `'We could not load your events.'` and was shown *"Failed to fetch"*
 *    instead, because the branch below used to prefer `error.message` over the fallback for every
 *    `Error`. It was true of every screen in all three consoles that reports a failure this way,
 *    and of the five uploads that go around the transport with a bare `fetch`, which is why this
 *    is fixed here rather than at the one screen that was reported.
 *
 *    A programming `TypeError` — *"x is not a function"* — lands here too, and that is correct for
 *    the same reason: it is not a sentence for a reader either.
 *
 * 3. **An error this product threw itself** keeps its own message. `'That upload did not finish.
 *    Try again.'` and the four like it across the SPAs are authored sentences, so a failure is not
 *    simply replaced by the fallback wholesale.
 */
export function errorToastMessageFor(error, fallback) {
    if (error instanceof ApiRequestError)
        return error.toUserMessage();
    if (error instanceof TypeError)
        return fallback ?? NETWORK_UNAVAILABLE;
    if (error instanceof Error && error.message !== '')
        return error.message;
    return fallback ?? SOMETHING_WENT_WRONG;
}
export function useErrorToast() {
    return React.useCallback((error, fallback) => {
        toast.error(errorToastMessageFor(error, fallback));
    }, []);
}
//# sourceMappingURL=use-error-toast.js.map